import { ConditionRules } from './core/conditions';
import { defaultStandardActionRules } from './core/standardActions';
import {
  RulesetBaseRules,
  RulesetDefinition,
  RulesetModule,
  RulesetOverlay,
  RulesetRuleOverrides,
} from '../types/Ruleset';

/**
 * Strict ruleset validation and deterministic overlay composition.
 *
 * This is the only runtime validator for the shared ruleset contract. API and
 * persistence layers call it instead of maintaining parallel schema guesses.
 * rule_reference: AGENTS.md strict validation, alternate rule modules, and no runtime defaults.
 * date_of_change: 2026-08-21
 */

export const RULESET_SCHEMA_VERSION = 1 as const;
export const RULESET_DOCUMENTATION_PATH = 'AGENTS.md#no-legacy-paths-or-fallbacks';

export interface RulesetValidationIssue {
  code: string;
  path: string;
  message: string;
  expected: string;
  received: unknown;
}

export interface RulesetValidationOptions {
  requireId?: boolean;
  registeredModuleSchemas?: ReadonlySet<string>;
}

export interface RulesetValidationResult {
  valid: boolean;
  issues: RulesetValidationIssue[];
}

export class RulesetContractError extends Error {
  readonly code = 'RULESET_CONTRACT_INVALID';
  readonly issues: RulesetValidationIssue[];
  readonly documentation = RULESET_DOCUMENTATION_PATH;

  constructor(issues: RulesetValidationIssue[]) {
    const paths = issues.map((issue) => issue.path).join(', ');
    super(`Ruleset contract is invalid at: ${paths}. See ${RULESET_DOCUMENTATION_PATH}.`);
    this.name = 'RulesetContractError';
    this.issues = issues;
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const addIssue = (
  issues: RulesetValidationIssue[],
  code: string,
  path: string,
  expected: string,
  received: unknown,
): void => {
  issues.push({
    code,
    path,
    message: `${path} must be ${expected}`,
    expected,
    received,
  });
};

const rejectUnknownKeys = (
  value: Record<string, unknown>,
  allowed: readonly string[],
  path: string,
  issues: RulesetValidationIssue[],
): void => {
  const allowedKeys = new Set(allowed);
  Object.keys(value).forEach((key) => {
    if (!allowedKeys.has(key)) {
      addIssue(issues, 'UNKNOWN_FIELD', `${path}.${key}`, `one of: ${allowed.join(', ')}`, value[key]);
    }
  });
};

const requireRecord = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
): value is Record<string, unknown> => {
  if (!isRecord(value)) {
    addIssue(issues, 'INVALID_OBJECT', path, 'an object', value);
    return false;
  }
  return true;
};

const requireString = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
  options: { minLength?: number; pattern?: RegExp } = {},
): void => {
  if (typeof value !== 'string' || value.trim().length < (options.minLength ?? 1)) {
    addIssue(issues, 'INVALID_STRING', path, 'a non-empty string', value);
    return;
  }
  if (options.pattern && !options.pattern.test(value)) {
    addIssue(issues, 'INVALID_FORMAT', path, `a string matching ${options.pattern}`, value);
  }
};

const requireBoolean = (value: unknown, path: string, issues: RulesetValidationIssue[]): void => {
  if (typeof value !== 'boolean') addIssue(issues, 'INVALID_BOOLEAN', path, 'a boolean', value);
};

const requireNumber = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
  options: { min?: number; max?: number; integer?: boolean } = {},
): void => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    addIssue(issues, 'INVALID_NUMBER', path, 'a finite number', value);
    return;
  }
  if (options.integer && !Number.isInteger(value)) {
    addIssue(issues, 'INVALID_INTEGER', path, 'an integer', value);
  }
  if (options.min != null && value < options.min) {
    addIssue(issues, 'NUMBER_TOO_SMALL', path, `at least ${options.min}`, value);
  }
  if (options.max != null && value > options.max) {
    addIssue(issues, 'NUMBER_TOO_LARGE', path, `at most ${options.max}`, value);
  }
};

const requireEnum = (
  value: unknown,
  allowed: readonly string[],
  path: string,
  issues: RulesetValidationIssue[],
): void => {
  if (typeof value !== 'string' || !allowed.includes(value)) {
    addIssue(issues, 'INVALID_ENUM', path, `one of: ${allowed.join(', ')}`, value);
  }
};

const validateJsonValue = (value: unknown, path: string, issues: RulesetValidationIssue[]): void => {
  if (value == null || ['string', 'number', 'boolean'].includes(typeof value)) {
    if (typeof value === 'number' && !Number.isFinite(value)) {
      addIssue(issues, 'INVALID_JSON_NUMBER', path, 'a finite JSON number', value);
    }
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((entry, index) => validateJsonValue(entry, `${path}[${index}]`, issues));
    return;
  }
  if (isRecord(value)) {
    Object.entries(value).forEach(([key, entry]) => validateJsonValue(entry, `${path}.${key}`, issues));
    return;
  }
  addIssue(issues, 'INVALID_JSON_VALUE', path, 'a JSON value', value);
};

const validateConditionEffect = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
): void => {
  if (!requireRecord(value, path, issues)) return;
  const kind = value.kind;
  const noArgumentKinds = ['cannot_act', 'cannot_react', 'cannot_move'];
  const scopedKinds = ['attack_advantage', 'attack_disadvantage'];
  const savingThrowKinds = ['saving_throw_advantage', 'saving_throw_disadvantage'];
  const allKinds = [...noArgumentKinds, 'speed_multiplier', ...scopedKinds, ...savingThrowKinds];
  requireEnum(kind, allKinds, `${path}.kind`, issues);

  if (noArgumentKinds.includes(String(kind))) {
    rejectUnknownKeys(value, ['kind'], path, issues);
  } else if (kind === 'speed_multiplier') {
    rejectUnknownKeys(value, ['kind', 'value'], path, issues);
    requireNumber(value.value, `${path}.value`, issues, { min: 0 });
  } else if (scopedKinds.includes(String(kind))) {
    rejectUnknownKeys(value, ['kind', 'scope'], path, issues);
    requireEnum(
      value.scope,
      ['attacks_by_creature', 'attacks_against_creature'],
      `${path}.scope`,
      issues,
    );
  } else if (savingThrowKinds.includes(String(kind))) {
    rejectUnknownKeys(value, ['kind', 'abilities'], path, issues);
    if (!Array.isArray(value.abilities) || value.abilities.length === 0) {
      addIssue(issues, 'INVALID_ARRAY', `${path}.abilities`, 'a non-empty array', value.abilities);
    } else {
      value.abilities.forEach((ability, index) => requireString(ability, `${path}.abilities[${index}]`, issues));
    }
  }
};

const validateConditionRule = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
): void => {
  if (!requireRecord(value, path, issues)) return;
  rejectUnknownKeys(value, ['description', 'effects'], path, issues);
  requireString(value.description, `${path}.description`, issues);
  if (!Array.isArray(value.effects)) {
    addIssue(issues, 'INVALID_ARRAY', `${path}.effects`, 'an array', value.effects);
  } else {
    value.effects.forEach((effect, index) => validateConditionEffect(effect, `${path}.effects[${index}]`, issues));
  }
};

const validateStandardActionRule = (
  value: unknown,
  expectedKey: string,
  path: string,
  issues: RulesetValidationIssue[],
  partial = false,
): void => {
  if (!requireRecord(value, path, issues)) return;
  const allowed = ['key', 'name', 'description', 'actionType', 'activationType', 'targetRequirement', 'executionRoute', 'rangeFeet', 'ruleReference', 'effectTags'];
  rejectUnknownKeys(value, allowed, path, issues);
  if (!partial || value.key !== undefined) {
    if (value.key !== expectedKey) addIssue(issues, 'ACTION_KEY_MISMATCH', `${path}.key`, expectedKey, value.key);
  }
  if (!partial || value.name !== undefined) requireString(value.name, `${path}.name`, issues);
  if (!partial || value.description !== undefined) requireString(value.description, `${path}.description`, issues);
  if (!partial || value.actionType !== undefined) {
    requireEnum(value.actionType, ['action', 'bonus', 'movement', 'free', 'object'], `${path}.actionType`, issues);
  }
  if (!partial || value.activationType !== undefined) {
    requireEnum(value.activationType, ['action', 'bonus action', 'movement', 'free', 'free object'], `${path}.activationType`, issues);
  }
  if (!partial || value.targetRequirement !== undefined) {
    requireEnum(value.targetRequirement, ['none', 'creature', 'object', 'trigger'], `${path}.targetRequirement`, issues);
  }
  if (!partial || value.executionRoute !== undefined) {
    requireEnum(value.executionRoute, ['standard'], `${path}.executionRoute`, issues);
  }
  if (value.rangeFeet !== undefined) {
    requireNumber(value.rangeFeet, `${path}.rangeFeet`, issues, { min: 0 });
  }
  if (!partial || value.ruleReference !== undefined) requireString(value.ruleReference, `${path}.ruleReference`, issues);
  if (!partial || value.effectTags !== undefined) {
    if (!Array.isArray(value.effectTags)) {
      addIssue(issues, 'INVALID_ARRAY', `${path}.effectTags`, 'an array of strings', value.effectTags);
    } else {
      value.effectTags.forEach((tag, index) => requireString(tag, `${path}.effectTags[${index}]`, issues));
    }
  }
};

const validateAttackRules = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
  partial = false,
): void => {
  if (!requireRecord(value, path, issues)) return;
  rejectUnknownKeys(
    value,
    ['attackRollModifier', 'damageRollModifier', 'naturalOneAlwaysMisses', 'naturalTwentyAlwaysHits', 'criticalHit'],
    path,
    issues,
  );
  if (!partial || value.attackRollModifier !== undefined) requireNumber(value.attackRollModifier, `${path}.attackRollModifier`, issues);
  if (!partial || value.damageRollModifier !== undefined) requireNumber(value.damageRollModifier, `${path}.damageRollModifier`, issues);
  if (!partial || value.naturalOneAlwaysMisses !== undefined) requireBoolean(value.naturalOneAlwaysMisses, `${path}.naturalOneAlwaysMisses`, issues);
  if (!partial || value.naturalTwentyAlwaysHits !== undefined) requireBoolean(value.naturalTwentyAlwaysHits, `${path}.naturalTwentyAlwaysHits`, issues);
  if (!partial || value.criticalHit !== undefined) {
    const criticalPath = `${path}.criticalHit`;
    if (requireRecord(value.criticalHit, criticalPath, issues)) {
      rejectUnknownKeys(value.criticalHit, ['threshold', 'damageDiceMultiplier'], criticalPath, issues);
      if (!partial || value.criticalHit.threshold !== undefined) {
        requireNumber(value.criticalHit.threshold, `${criticalPath}.threshold`, issues, { min: 1, max: 20, integer: true });
      }
      if (!partial || value.criticalHit.damageDiceMultiplier !== undefined) {
        requireNumber(value.criticalHit.damageDiceMultiplier, `${criticalPath}.damageDiceMultiplier`, issues, { min: 1, integer: true });
      }
    }
  }
};

const validateSpellcastingRules = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
  partial = false,
): void => {
  if (!requireRecord(value, path, issues)) return;
  rejectUnknownKeys(value, ['requireSpellSlots', 'requirePreparedOrKnownSpell', 'requireComponents', 'concentration'], path, issues);
  ['requireSpellSlots', 'requirePreparedOrKnownSpell', 'requireComponents'].forEach((key) => {
    if (!partial || value[key] !== undefined) requireBoolean(value[key], `${path}.${key}`, issues);
  });
  if (!partial || value.concentration !== undefined) {
    const concentrationPath = `${path}.concentration`;
    if (requireRecord(value.concentration, concentrationPath, issues)) {
      rejectUnknownKeys(value.concentration, ['enabled', 'baseSaveDC', 'damageDivisor', 'rounding'], concentrationPath, issues);
      if (!partial || value.concentration.enabled !== undefined) requireBoolean(value.concentration.enabled, `${concentrationPath}.enabled`, issues);
      if (!partial || value.concentration.baseSaveDC !== undefined) requireNumber(value.concentration.baseSaveDC, `${concentrationPath}.baseSaveDC`, issues, { min: 1, integer: true });
      if (!partial || value.concentration.damageDivisor !== undefined) requireNumber(value.concentration.damageDivisor, `${concentrationPath}.damageDivisor`, issues, { min: 1 });
      if (!partial || value.concentration.rounding !== undefined) requireEnum(value.concentration.rounding, ['floor', 'ceil'], `${concentrationPath}.rounding`, issues);
    }
  }
};

const validateGrappleRules = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
  partial = false,
): void => {
  if (!requireRecord(value, path, issues)) return;
  rejectUnknownKeys(value, ['resolution', 'attackerAbility', 'defenderAbilities', 'sizeLimitDifference', 'escapeActionCost', 'dragSpeedMultiplier'], path, issues);
  if (!partial || value.resolution !== undefined) requireEnum(value.resolution, ['contested_check', 'saving_throw'], `${path}.resolution`, issues);
  if (!partial || value.attackerAbility !== undefined) requireEnum(value.attackerAbility, ['strength'], `${path}.attackerAbility`, issues);
  if (!partial || value.defenderAbilities !== undefined) {
    if (!Array.isArray(value.defenderAbilities) || value.defenderAbilities.length === 0) {
      addIssue(issues, 'INVALID_ARRAY', `${path}.defenderAbilities`, 'a non-empty ability array', value.defenderAbilities);
    } else {
      value.defenderAbilities.forEach((ability, index) => requireEnum(ability, ['strength', 'dexterity'], `${path}.defenderAbilities[${index}]`, issues));
    }
  }
  if (!partial || value.sizeLimitDifference !== undefined) requireNumber(value.sizeLimitDifference, `${path}.sizeLimitDifference`, issues, { min: 0, integer: true });
  if (!partial || value.escapeActionCost !== undefined) requireEnum(value.escapeActionCost, ['action'], `${path}.escapeActionCost`, issues);
  if (!partial || value.dragSpeedMultiplier !== undefined) requireNumber(value.dragSpeedMultiplier, `${path}.dragSpeedMultiplier`, issues, { min: 0, max: 1 });
};

const validateMovementRules = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
  partial = false,
): void => {
  if (!requireRecord(value, path, issues)) return;
  rejectUnknownKeys(value, ['difficultTerrainMultiplier', 'diagonalMethod', 'forcedMovementProvokesOpportunityAttacks'], path, issues);
  if (!partial || value.difficultTerrainMultiplier !== undefined) requireNumber(value.difficultTerrainMultiplier, `${path}.difficultTerrainMultiplier`, issues, { min: 1 });
  if (!partial || value.diagonalMethod !== undefined) requireEnum(value.diagonalMethod, ['euclidean', 'five_ten_five'], `${path}.diagonalMethod`, issues);
  if (!partial || value.forcedMovementProvokesOpportunityAttacks !== undefined) requireBoolean(value.forcedMovementProvokesOpportunityAttacks, `${path}.forcedMovementProvokesOpportunityAttacks`, issues);
};

const validateRestRules = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
  partial = false,
): void => {
  if (!requireRecord(value, path, issues)) return;
  rejectUnknownKeys(value, ['shortRestMinutes', 'shortRestAllowsHitDice', 'longRestHours', 'longRestRestoresAllHitPoints', 'longRestHitDiceRecoveryDivisor'], path, issues);
  if (!partial || value.shortRestMinutes !== undefined) requireNumber(value.shortRestMinutes, `${path}.shortRestMinutes`, issues, { min: 1 });
  if (!partial || value.shortRestAllowsHitDice !== undefined) requireBoolean(value.shortRestAllowsHitDice, `${path}.shortRestAllowsHitDice`, issues);
  if (!partial || value.longRestHours !== undefined) requireNumber(value.longRestHours, `${path}.longRestHours`, issues, { min: 1 });
  if (!partial || value.longRestRestoresAllHitPoints !== undefined) requireBoolean(value.longRestRestoresAllHitPoints, `${path}.longRestRestoresAllHitPoints`, issues);
  if (!partial || value.longRestHitDiceRecoveryDivisor !== undefined) requireNumber(value.longRestHitDiceRecoveryDivisor, `${path}.longRestHitDiceRecoveryDivisor`, issues, { min: 1 });
};

const validateConditions = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
): void => {
  if (!requireRecord(value, path, issues)) return;
  Object.entries(value).forEach(([key, rule]) => validateConditionRule(rule, `${path}.${key}`, issues));
};

const validateStandardActions = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
  partial = false,
): void => {
  if (!requireRecord(value, path, issues)) return;
  const expectedKeys = Object.keys(defaultStandardActionRules);
  rejectUnknownKeys(value, expectedKeys, path, issues);
  if (!partial) {
    expectedKeys.forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(value, key)) {
        addIssue(issues, 'MISSING_FIELD', `${path}.${key}`, 'a complete standard action rule', undefined);
      }
    });
  }
  Object.entries(value).forEach(([key, rule]) => validateStandardActionRule(rule, key, `${path}.${key}`, issues, partial));
};

const validateBaseRules = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
): void => {
  if (!requireRecord(value, path, issues)) return;
  rejectUnknownKeys(value, ['attack', 'spellcasting', 'grapple', 'conditions', 'movement', 'rests', 'standardActions'], path, issues);
  validateAttackRules(value.attack, `${path}.attack`, issues);
  validateSpellcastingRules(value.spellcasting, `${path}.spellcasting`, issues);
  validateGrappleRules(value.grapple, `${path}.grapple`, issues);
  validateConditions(value.conditions, `${path}.conditions`, issues);
  validateMovementRules(value.movement, `${path}.movement`, issues);
  validateRestRules(value.rests, `${path}.rests`, issues);
  validateStandardActions(value.standardActions, `${path}.standardActions`, issues);
};

const validateModule = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
  registeredModuleSchemas?: ReadonlySet<string>,
): void => {
  if (!requireRecord(value, path, issues)) return;
  rejectUnknownKeys(value, ['id', 'name', 'version', 'schemaId', 'enabled', 'config'], path, issues);
  requireString(value.id, `${path}.id`, issues);
  requireString(value.name, `${path}.name`, issues);
  requireString(value.version, `${path}.version`, issues, { pattern: /^\d+\.\d+\.\d+$/ });
  requireString(value.schemaId, `${path}.schemaId`, issues);
  requireBoolean(value.enabled, `${path}.enabled`, issues);
  if (requireRecord(value.config, `${path}.config`, issues)) {
    validateJsonValue(value.config, `${path}.config`, issues);
  }
  if (
    value.enabled === true
    && registeredModuleSchemas
    && typeof value.schemaId === 'string'
    && !registeredModuleSchemas.has(value.schemaId)
  ) {
    addIssue(issues, 'UNREGISTERED_MODULE_SCHEMA', `${path}.schemaId`, 'a registered module schema ID', value.schemaId);
  }
};

const validateModuleArray = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
  registeredModuleSchemas?: ReadonlySet<string>,
): void => {
  if (!Array.isArray(value)) {
    addIssue(issues, 'INVALID_ARRAY', path, 'an array', value);
    return;
  }
  const ids = new Set<string>();
  value.forEach((module, index) => {
    validateModule(module, `${path}[${index}]`, issues, registeredModuleSchemas);
    const id = isRecord(module) && typeof module.id === 'string' ? module.id : null;
    if (id && ids.has(id)) addIssue(issues, 'DUPLICATE_MODULE_ID', `${path}[${index}].id`, 'a unique module ID', id);
    if (id) ids.add(id);
  });
};

const validateOverrides = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
): void => {
  if (!requireRecord(value, path, issues)) return;
  rejectUnknownKeys(value, ['attack', 'spellcasting', 'grapple', 'conditions', 'movement', 'rests', 'standardActions'], path, issues);
  if (value.attack !== undefined) validateAttackRules(value.attack, `${path}.attack`, issues, true);
  if (value.spellcasting !== undefined) validateSpellcastingRules(value.spellcasting, `${path}.spellcasting`, issues, true);
  if (value.grapple !== undefined) validateGrappleRules(value.grapple, `${path}.grapple`, issues, true);
  if (value.conditions !== undefined) validateConditions(value.conditions, `${path}.conditions`, issues);
  if (value.movement !== undefined) validateMovementRules(value.movement, `${path}.movement`, issues, true);
  if (value.rests !== undefined) validateRestRules(value.rests, `${path}.rests`, issues, true);
  if (value.standardActions !== undefined) validateStandardActions(value.standardActions, `${path}.standardActions`, issues, true);
};

const validateOverlay = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
  registeredModuleSchemas?: ReadonlySet<string>,
): void => {
  if (!requireRecord(value, path, issues)) return;
  rejectUnknownKeys(value, ['id', 'name', 'version', 'enabled', 'priority', 'overrides', 'modules'], path, issues);
  requireString(value.id, `${path}.id`, issues);
  requireString(value.name, `${path}.name`, issues);
  requireString(value.version, `${path}.version`, issues, { pattern: /^\d+\.\d+\.\d+$/ });
  requireBoolean(value.enabled, `${path}.enabled`, issues);
  requireNumber(value.priority, `${path}.priority`, issues, { integer: true });
  validateOverrides(value.overrides, `${path}.overrides`, issues);
  validateModuleArray(value.modules, `${path}.modules`, issues, registeredModuleSchemas);
};

const validateOverlayArray = (
  value: unknown,
  path: string,
  issues: RulesetValidationIssue[],
  registeredModuleSchemas?: ReadonlySet<string>,
): void => {
  if (!Array.isArray(value)) {
    addIssue(issues, 'INVALID_ARRAY', path, 'an array', value);
    return;
  }
  const ids = new Set<string>();
  value.forEach((overlay, index) => {
    validateOverlay(overlay, `${path}[${index}]`, issues, registeredModuleSchemas);
    const id = isRecord(overlay) && typeof overlay.id === 'string' ? overlay.id : null;
    if (id && ids.has(id)) addIssue(issues, 'DUPLICATE_OVERLAY_ID', `${path}[${index}].id`, 'a unique overlay ID', id);
    if (id) ids.add(id);
  });
};

export const validateRulesetDefinition = (
  value: unknown,
  options: RulesetValidationOptions = {},
): RulesetValidationResult => {
  const issues: RulesetValidationIssue[] = [];
  const registeredModuleSchemas = options.registeredModuleSchemas ?? new Set<string>();
  if (!requireRecord(value, 'ruleset', issues)) return { valid: false, issues };

  const definitionKeys = ['schemaVersion', 'id', 'name', 'description', 'version', 'edition', 'baseRules', 'modules', 'overlays'];
  rejectUnknownKeys(value, definitionKeys, 'ruleset', issues);

  if (value.schemaVersion !== RULESET_SCHEMA_VERSION) {
    addIssue(issues, 'UNSUPPORTED_SCHEMA_VERSION', 'ruleset.schemaVersion', String(RULESET_SCHEMA_VERSION), value.schemaVersion);
  }
  if (options.requireId !== false) requireString(value.id ?? value._id, 'ruleset.id', issues);
  requireString(value.name, 'ruleset.name', issues, { minLength: 3 });
  requireString(value.description, 'ruleset.description', issues);
  requireString(value.version, 'ruleset.version', issues, { pattern: /^\d+\.\d+\.\d+$/ });
  requireEnum(value.edition, ['2014', '2024', 'custom'], 'ruleset.edition', issues);
  validateBaseRules(value.baseRules, 'ruleset.baseRules', issues);
  validateModuleArray(value.modules, 'ruleset.modules', issues, registeredModuleSchemas);
  validateOverlayArray(value.overlays, 'ruleset.overlays', issues, registeredModuleSchemas);

  return { valid: issues.length === 0, issues };
};

export function assertRulesetDefinition(
  value: unknown,
  options: RulesetValidationOptions = {},
): asserts value is RulesetDefinition {
  const validation = validateRulesetDefinition(value, options);
  if (!validation.valid) throw new RulesetContractError(validation.issues);
}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const mergeModules = (base: RulesetModule[], additions: RulesetModule[]): RulesetModule[] => {
  const modules = new Map(base.map((module) => [module.id, clone(module)]));
  additions.forEach((module) => modules.set(module.id, clone(module)));
  return Array.from(modules.values());
};

const applyOverrides = (base: RulesetBaseRules, overrides: RulesetRuleOverrides): RulesetBaseRules => ({
  ...base,
  attack: {
    ...base.attack,
    ...(overrides.attack || {}),
    criticalHit: {
      ...base.attack.criticalHit,
      ...(overrides.attack?.criticalHit || {}),
    },
  },
  spellcasting: {
    ...base.spellcasting,
    ...(overrides.spellcasting || {}),
    concentration: {
      ...base.spellcasting.concentration,
      ...(overrides.spellcasting?.concentration || {}),
    },
  },
  grapple: { ...base.grapple, ...(overrides.grapple || {}) },
  conditions: { ...base.conditions, ...(overrides.conditions || {}) } as ConditionRules,
  movement: { ...base.movement, ...(overrides.movement || {}) },
  rests: { ...base.rests, ...(overrides.rests || {}) },
  standardActions: {
    ...base.standardActions,
    ...(overrides.standardActions || {}),
  },
});

export interface ResolvedRuleset extends Omit<RulesetDefinition, 'overlays'> {
  overlays: RulesetOverlay[];
  appliedOverlayIds: string[];
}

export const resolveRuleset = (
  ruleset: RulesetDefinition,
  registeredModuleSchemas?: ReadonlySet<string>,
): ResolvedRuleset => {
  assertRulesetDefinition(ruleset, { registeredModuleSchemas });
  const enabledOverlays = ruleset.overlays
    .filter((overlay) => overlay.enabled)
    .slice()
    .sort((left, right) => left.priority - right.priority || left.id.localeCompare(right.id));

  let baseRules = clone(ruleset.baseRules);
  let modules = clone(ruleset.modules);
  enabledOverlays.forEach((overlay) => {
    baseRules = applyOverrides(baseRules, overlay.overrides);
    modules = mergeModules(modules, overlay.modules);
  });

  const resolvedDefinition: RulesetDefinition = {
    ...clone(ruleset),
    baseRules,
    modules,
  };
  const finalValidation = validateRulesetDefinition(resolvedDefinition, { registeredModuleSchemas });
  if (!finalValidation.valid) throw new RulesetContractError(finalValidation.issues);
  return {
    ...resolvedDefinition,
    appliedOverlayIds: enabledOverlays.map((overlay) => overlay.id),
  };
};
